import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "../lib/cn.js";
import type { AppShell } from "../lib/shell.js";
import type { QuickLink } from "../lib/quickLinks.js";
import { ThemeToggle } from "./ui/ThemeToggle.js";
import { getSessionToken } from "../hooks/useSession.js";
import { requestSurelcLogin, submitSurelcForm } from "../lib/surelcSso.js";

interface AppNavbarProps {
  agentEmail: string;
  organizationName: string;
  agentRole?: "ADMIN" | "PPG" | "PROVIDER" | "AGENT";
  isStaff?: boolean;
  shell: AppShell;
  contractsEnabled?: boolean;
  downlinesEnabled?: boolean;
  quickLinks?: QuickLink[];
  onSignOut: () => void;
}

function navLinkClass(isActive: boolean, drawer: boolean) {
  return cn(
    "nav-link",
    !drawer && "md:justify-center xl:justify-start",
    isActive ? "nav-link-active" : "nav-link-inactive"
  );
}

function blurOnNavigate(event: { currentTarget: HTMLAnchorElement }) {
  event.currentTarget.blur();
}

function NavbarNavLink({ onClick, ...props }: ComponentProps<typeof NavLink>) {
  return (
    <NavLink
      {...props}
      onClick={(event) => {
        onClick?.(event);
        blurOnNavigate(event);
      }}
    />
  );
}

function OrganizationLogo({ name }: { name: string }) {
  return (
    <div className="altura-logo-mark flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] font-display text-base font-extrabold text-white">
      {name.trim().charAt(0).toUpperCase() || "A"}
    </div>
  );
}

const icons: Record<string, ReactNode> = {
  home: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m3 11 9-7 9 7v9H3v-9Zm6 9v-6h6v6" />,
  agents: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 20a4 4 0 0 0-8 0m8 0h5v-1a4 4 0 0 0-6-3.46M16 20H8m0 0H3v-1a4 4 0 0 1 6-3.46M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm5 2a2.5 2.5 0 1 1-3-2.45M4 9a2.5 2.5 0 1 0 3-2.45" />,
  contracts: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M6 3h9l3 3v15H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm9 0v4h4M8 12h7M8 16h7" />,
  operations: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 5h16v14H4V5Zm0 4h16M8 13h3M8 16h6" />,
  services: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m12 3 2 4 4 .6-3 3 .7 4.4-3.7-2-3.7 2 .7-4.4-3-3L10 7l2-4Zm-7 16h14" />,
  flags: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 4v16m0-16h9l-1.5 3L14 10H5" />,
  users: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 19a5 5 0 0 0-10 0m5-8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm10 8v-2a4 4 0 0 0-4-4" />,
  requests: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 4h14v16H5V4Zm4 0V2m6 2V2M8 9h8m-8 4h5" />,
  artifacts: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16v14H4V6Zm4 0V3h8v3m-7 5h6m-6 4h4" />,
  sms: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 4h16v13H8l-4 4V4Zm4 5h8m-8 4h5" />,
  sync: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M20 7h-5V2m5 5-4-4a8 8 0 1 0 2 12M4 17h5v5m-5-5 4 4" />,
  account: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm7 8a7 7 0 0 0-14 0" />,
  tasks: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5h11M9 12h11M9 19h11M4 5l1 1 2-2M4 12l1 1 2-2M4 19l1 1 2-2" />,
  inbox: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16v12H4V6Zm0 1 8 6 8-6" />,
  changeRequest: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M6 3h8l4 4v6M6 3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4M14 3v4h4M8 11h5m-5 4h3m5.5 5.5 4.5-4.5-2-2-4.5 4.5-.5 2.5 2.5-.5Z" />,
  hub: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 3v4m0 10v4M3 12h4m10 0h4M8.5 8.5 5.6 5.6m9.9 2.9 2.9-2.9m-9.9 9.9-2.9 2.9m9.9-2.9 2.9 2.9M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />,
  reagan: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 4.5 8.2 8l3.5 1.2L8.2 10.4 7 13.9 5.8 10.4 2.3 9.2 5.8 8 7 4.5Zm9 3.6 1.7 4.7 4.7 1.7-4.7 1.7L16 21l-1.7-4.8-4.7-1.7 4.7-1.7L16 8.1Z" />,
  surelc: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M14 4h6v6m0-6-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />,
  discord: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8.6 16.8c-1.6-.3-3-.9-4-1.7.3-3.7 1.3-6.7 2.6-8.4A9 9 0 0 1 9.8 5.7l.6 1.4a13 13 0 0 1 3.2 0l.6-1.4a9 9 0 0 1 2.6 1c1.3 1.7 2.3 4.7 2.6 8.4-1 .8-2.4 1.4-4 1.7l-.8-1.5m-4.6 0-.8 1.5M10 12.2h0m4 0h0" />,
};

function NavIcon({ name }: { name: keyof typeof icons }) {
  return <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>{icons[name]}</svg>;
}

function SideLink({ to, label, icon, drawer = false, end }: { to: string; label: string; icon: keyof typeof icons; drawer?: boolean; end?: boolean }) {
  return (
    <NavbarNavLink
      to={to}
      end={end}
      className={({ isActive }) => navLinkClass(isActive, drawer)}
      title={drawer ? undefined : label}
      aria-label={drawer ? undefined : label}
    >
      <NavIcon name={icon} />
      <span className={drawer ? "" : "md:hidden xl:inline"}>{label}</span>
    </NavbarNavLink>
  );
}

function ExternalSideLink({ href, label, icon, drawer = false }: { href: string; label: string; icon: keyof typeof icons; drawer?: boolean }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={navLinkClass(false, drawer)}
      title={drawer ? undefined : label}
      aria-label={`${label} (opens in a new tab)`}
    >
      <NavIcon name={icon} />
      <span className={drawer ? "" : "md:hidden xl:inline"}>{label}</span>
    </a>
  );
}

/**
 * Opens SureLC in a new tab, signed in through Altura's SSO (Paul, Oct 29: a
 * sidebar link in both the agent and admin portals, like the Reagan one). The
 * tab opens first so the browser does not block it, then receives the signed
 * login form.
 */
function SurelcSideLink({ drawer = false }: { drawer?: boolean }) {
  const [opening, setOpening] = useState(false);
  async function open() {
    const token = getSessionToken();
    if (!token || opening) return;
    const popup = window.open("", "surelc-sso");
    if (!popup) {
      window.alert("Allow pop-ups for this site, then open SureLC again.");
      return;
    }
    setOpening(true);
    try {
      submitSurelcForm(popup, await requestSurelcLogin(token));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to open SureLC";
      popup.document.body.textContent = `SureLC could not be opened: ${message}`;
    } finally {
      setOpening(false);
    }
  }
  return (
    <button
      type="button"
      onClick={() => void open()}
      disabled={opening}
      className={cn(navLinkClass(false, drawer), "w-full")}
      title={drawer ? undefined : "SureLC"}
      aria-label={drawer ? undefined : "SureLC (opens in a new tab)"}
    >
      <NavIcon name="surelc" />
      <span className={drawer ? "" : "md:hidden xl:inline"}>{opening ? "Opening SureLC…" : "SureLC"}</span>
    </button>
  );
}

function QuickLinksSection({ quickLinks, drawer = false }: { quickLinks: QuickLink[]; drawer?: boolean }) {
  if (quickLinks.length === 0) return null;
  return (
    <div className="mt-4 space-y-1 border-t border-border-subtle pt-4">
      <p className={cn("px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.13em] text-content-muted", !drawer && "md:hidden xl:block")}>
        Quick links
      </p>
      {quickLinks.map((link) => (
        <ExternalSideLink key={link.href} href={link.href} label={link.label} icon={link.icon} drawer={drawer} />
      ))}
    </div>
  );
}

function StaffNavLinks({ agentRole, isPpgUser, drawer = false }: { agentRole?: string; isPpgUser: boolean; drawer?: boolean }) {
  return (
    <>
      <SideLink to="/dashboard/agents" label={isPpgUser ? "Contract queue" : "Agents"} icon="agents" drawer={drawer} />
      {!isPpgUser && agentRole === "ADMIN" && (
        <SideLink to="/dashboard/submissions" label="Operations" icon="operations" drawer={drawer} end />
      )}
      {agentRole === "ADMIN" && (
        <SideLink to="/dashboard/services" label="Services" icon="services" drawer={drawer} />
      )}
      {agentRole === "ADMIN" && (
        <SideLink to="/dashboard/feature-flags" label="Feature flags" icon="flags" drawer={drawer} />
      )}
      {agentRole === "ADMIN" && (
        <>
          <SideLink to="/dashboard/users" label="Users" icon="users" drawer={drawer} />
          <SideLink to="/dashboard/carriers" label="Carriers" icon="contracts" drawer={drawer} />
          <SideLink to="/dashboard/agent-requests" label="Requests" icon="requests" drawer={drawer} />
          <SideLink to="/dashboard/inbox" label="Inbox" icon="inbox" drawer={drawer} />
          <SideLink to="/dashboard/onboarding-tasks" label="Onboarding tasks" icon="tasks" drawer={drawer} />
          <SurelcSideLink drawer={drawer} />
        </>
      )}
      {!isPpgUser && agentRole === "ADMIN" && (
        <>
          <SideLink to="/run-artifacts" label="Artifacts" icon="artifacts" drawer={drawer} />
          <SideLink to="/sms-uplines" label="SMS uplines" icon="sms" drawer={drawer} />
          <SideLink to="/dashboard/sync-health" label="Sync health" icon="sync" drawer={drawer} />
        </>
      )}
    </>
  );
}

export function AppNavbar({
  agentEmail,
  organizationName,
  agentRole,
  shell,
  contractsEnabled = true,
  downlinesEnabled = false,
  quickLinks = [],
  onSignOut,
}: AppNavbarProps) {
  const isPpgUser = agentRole === "PPG";
  const isAgentPortal = shell === "portal";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const accountPath = isAgentPortal ? "/my-agent/account" : "/dashboard/account";
  const roleLabel = isAgentPortal ? "Agent portal" : shell === "provider" ? "Provider workspace" : "Admin workspace";

  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(drawerRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      ) ?? []);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", close);
      menuButtonRef.current?.focus();
    };
  }, [menuOpen]);

  const links = (drawer = false) => (
    <>
      {isAgentPortal ? (
        <>
          <SideLink to="/my-agent" label="Setup" icon="home" drawer={drawer} end />
          {contractsEnabled && (
            <SideLink to="/my-agent/contracts" label="Contracts" icon="contracts" drawer={drawer} />
          )}
          {downlinesEnabled && (
            <>
              <SideLink to="/my-agent/downlines" label="Downlines" icon="agents" drawer={drawer} />
              <SideLink
                to="/my-agent/change-request"
                label="Change request"
                icon="changeRequest"
                drawer={drawer}
              />
            </>
          )}
          {contractsEnabled && <SurelcSideLink drawer={drawer} />}
        </>
      ) : (
        <StaffNavLinks agentRole={agentRole} isPpgUser={isPpgUser} drawer={drawer} />
      )}
      <QuickLinksSection quickLinks={quickLinks} drawer={drawer} />
    </>
  );

  return (
    <>
      <header className="app-utility-bar fixed inset-x-0 top-0 z-40 flex h-16 items-center md:left-20 xl:left-[15.5rem]">
        <div className="flex w-full items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button ref={menuButtonRef} type="button" onClick={() => setMenuOpen(true)} className="focus-ring inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border-default bg-surface-raised text-content-primary md:hidden" aria-label="Open navigation" aria-expanded={menuOpen} aria-controls="mobile-navigation-drawer">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h16" /></svg>
            </button>
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold uppercase tracking-[0.12em] text-content-muted">{roleLabel}</p>
              <p className="truncate text-sm font-semibold text-content-primary">{agentEmail}</p>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <aside className="app-sidebar fixed inset-y-0 left-0 z-50 hidden w-20 flex-col md:flex xl:w-[15.5rem]" aria-label="Primary navigation">
        <div className="flex h-16 items-center gap-3 border-b border-border-subtle px-5 xl:px-6">
          <OrganizationLogo name={organizationName} />
          <div className="hidden min-w-0 xl:block">
            <p className="truncate font-display text-base font-extrabold leading-none text-content-primary">{organizationName}</p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.13em] text-content-muted">Agent management</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-5">{links()}</nav>
        <div className="space-y-1 border-t border-border-subtle p-3">
          <SideLink to={accountPath} label="Account" icon="account" />
          <button type="button" onClick={onSignOut} className="nav-link nav-link-inactive w-full md:justify-center xl:justify-start" title="Sign out">
            <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10 5H5v14h5m4-3 4-4-4-4m4 4H9" /></svg>
            <span className="md:hidden xl:inline">Sign out</span>
          </button>
        </div>
      </aside>

      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button type="button" className="absolute inset-0 bg-scrim" onClick={() => setMenuOpen(false)} aria-label="Close navigation" />
          <aside ref={drawerRef} id="mobile-navigation-drawer" className="app-sidebar relative flex h-full w-[min(19rem,88vw)] flex-col shadow-2xl" role="dialog" aria-modal="true" aria-label="Navigation menu">
            <div className="flex h-16 items-center justify-between border-b border-border-subtle px-5">
              <div className="flex min-w-0 items-center gap-3"><OrganizationLogo name={organizationName} /><span className="truncate font-display font-extrabold text-content-primary">{organizationName}</span></div>
              <button autoFocus type="button" onClick={() => setMenuOpen(false)} className="focus-ring flex h-10 w-10 items-center justify-center rounded-lg text-content-secondary" aria-label="Close navigation">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeWidth={2} d="m6 6 12 12M18 6 6 18" /></svg>
              </button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto p-4">{links(true)}</nav>
            <div className="space-y-1 border-t border-border-subtle p-4">
              <SideLink to={accountPath} label="Account" icon="account" drawer />
              <button type="button" onClick={onSignOut} className="nav-link nav-link-inactive w-full"><svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeWidth={1.8} d="M10 5H5v14h5m4-3 4-4-4-4m4 4H9" /></svg>Sign out</button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
